import { Component, Inject, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import { formatDate, LowerCasePipe } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EChartComponent } from '@shared/components/echart/echart.component';
import {
  DataHubMetricsService,
  ProcessSeriesDto,
  ProcessSeriesPointDto,
  ProcessWindowStatusDto,
} from '@app/services/datahub-metrics.service';
import { DATE_TIME_FORMAT } from '@app/core/constants/date-time-format';
import { Observable, Subscription } from 'rxjs';

interface TimelineSegment extends ProcessSeriesPointDto {}

interface TimelineRow {
  labelKey: string;
  loading: boolean;
  error: boolean;
  segments: TimelineSegment[];
  aggregate?: ProcessWindowStatusDto | null;
}

interface TimelineGroup {
  titleKey: string;
  rows: TimelineRow[];
  volumeOptions: any;
}

interface TooltipState {
  segment: TimelineSegment;
  x: number;
  y: number;
}

@Component({
  selector: 'app-dashboard-datahub-communication',
  standalone: true,
  imports: [TranslatePipe, LowerCasePipe, EChartComponent],
  templateUrl: './dashboard-datahub-communication.component.html',
})
export class DashboardDataHubCommunicationComponent implements OnInit, OnDestroy {

  public groups: TimelineGroup[] = [
    {
      titleKey: 'dashboard.datahub.communication.v3',
      volumeOptions: null,
      rows: [
        { labelKey: 'dashboard.datahub.communication.fetchV3', loading: true, error: false, segments: [] },
        { labelKey: 'dashboard.datahub.communication.sendV3',  loading: true, error: false, segments: [] },
      ],
    },
    {
      titleKey: 'dashboard.datahub.communication.v2',
      volumeOptions: null,
      rows: [
        { labelKey: 'dashboard.datahub.communication.fetchV2', loading: true, error: false, segments: [] },
        { labelKey: 'dashboard.datahub.communication.sendV2',  loading: true, error: false, segments: [] },
      ],
    },
  ];

  public tooltip: TooltipState | null = null;

  private readonly subs = new Subscription();

  constructor(
    private readonly metricsService: DataHubMetricsService,
    private readonly translate: TranslateService,
    @Inject(LOCALE_ID) private readonly locale: string,
  ) {}

  public ngOnInit(): void {
    // Aggregates — window-level stats for the stats line
    this.subs.add(
      this.metricsService.getAggregates(24).subscribe({
        next: (arr) => {
          const byName = Object.fromEntries(arr.map(x => [x.processName, x]));
          this.groups[0].rows[0].aggregate = byName['DataHubFetchV3'];
          this.groups[0].rows[1].aggregate = byName['DataHubSendV3'];
          this.groups[1].rows[0].aggregate = byName['DataHubFetchV2'];
          this.groups[1].rows[1].aggregate = byName['DataHubSendV2'];
        },
      })
    );

    // Series — per-hour points for the timeline bar and volume chart.
    // After each row resolves, check if the sibling row in the same group
    // is also done; if so, build that group's volume chart.
    const loadRow = (
      obs: Observable<ProcessSeriesDto | null>,
      row: TimelineRow,
      group: TimelineGroup,
    ): void => {
      this.subs.add(obs.subscribe({
        next: (series) => {
          if (series) {
            row.segments = [...(series.points ?? [])].sort(
              (a, b) => new Date(a.startUtc).getTime() - new Date(b.startUtc).getTime(),
            );
            row.loading = false;
          } else {
            row.loading = false;
            row.error = true;
          }
          if (group.rows.every(r => !r.loading)) {
            group.volumeOptions = this.buildVolumeOptions(
              group.rows[0].segments,
              group.rows[1].segments,
            );
          }
        },
        error: () => {
          row.loading = false;
          row.error = true;
          if (group.rows.every(r => !r.loading)) {
            group.volumeOptions = this.buildVolumeOptions(
              group.rows[0].segments,
              group.rows[1].segments,
            );
          }
        },
      }));
    };

    loadRow(this.metricsService.getFetchSeriesAggregate('V3', 24, 60), this.groups[0].rows[0], this.groups[0]);
    loadRow(this.metricsService.getSendSeriesAggregate('V3', 24, 60),  this.groups[0].rows[1], this.groups[0]);
    loadRow(this.metricsService.getFetchSeriesAggregate('V2', 24, 60), this.groups[1].rows[0], this.groups[1]);
    loadRow(this.metricsService.getSendSeriesAggregate('V2', 24, 60),  this.groups[1].rows[1], this.groups[1]);
  }

  public ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  // ─── Volume chart ────────────────────────────────────────────────────────────

  private buildVolumeOptions(receive: TimelineSegment[], send: TimelineSegment[]): any {
    const labels      = receive.map(p => this.fmtHour(p.startUtc));
    const recvSuccess = receive.map(p => p.successCount);
    const recvFailure = receive.map(p => p.failureCount);
    const sendSuccess = send.map(p => p.successCount);
    const sendFailure = send.map(p => p.failureCount);

    const receiveLabel = this.translate.instant('dashboard.datahub.communication.receive');
    const sendLabel    = this.translate.instant('dashboard.datahub.communication.send');
    const successLabel = this.translate.instant('dashboard.datahub.communication.success');
    const failureLabel = this.translate.instant('dashboard.datahub.communication.failures');

    // Highlight columns that have any failures with a faint red background
    const errorAreaData = recvFailure
      .map((_, i) => (recvFailure[i] > 0 || sendFailure[i] > 0 ? i : -1))
      .filter(i => i >= 0)
      .map(i => [{ xAxis: i - 0.5 }, { xAxis: i + 0.5 }]);

    const makeBar = (name: string, data: number[], color: string, stack: string, isFirst = false) => ({
      name,
      type: 'bar',
      stack,
      barMaxWidth: 20,
      itemStyle: { color },
      emphasis: { itemStyle: { opacity: 0.85 } },
      data,
      ...(isFirst && errorAreaData.length > 0 ? {
        markArea: {
          silent: true,
          itemStyle: { color: 'rgba(239,68,68,0.08)' },
          data: errorAreaData,
        },
      } : {}),
    });

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor: 'rgba(15,15,15,0.85)',
        borderColor: 'transparent',
        textStyle: { color: '#e5e7eb', fontSize: 12 },
        padding: 10,
        extraCssText: 'border-radius:8px',
      },
      legend: {
        bottom: 0,
        textStyle: { color: '#9ca3af', fontSize: 11 },
        itemWidth: 12, itemHeight: 8,
        data: [
          `${receiveLabel} — ${successLabel}`,
          `${receiveLabel} — ${failureLabel}`,
          `${sendLabel} — ${successLabel}`,
          `${sendLabel} — ${failureLabel}`,
        ],
      },
      grid: { left: '2%', right: '2%', top: '6%', bottom: '18%', containLabel: true },
      xAxis: {
        type: 'category',
        data: labels,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: '#9ca3af',
          fontSize: 10,
          interval: Math.max(0, Math.floor(labels.length / 8) - 1),
        },
      },
      yAxis: {
        type: 'value',
        minInterval: 1,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: '#9ca3af', fontSize: 10 },
        splitLine: { lineStyle: { color: 'rgba(0,0,0,0.06)' } },
      },
      series: [
        makeBar(`${receiveLabel} — ${successLabel}`, recvSuccess, 'rgba(16,185,129,0.80)', 'receive', true),
        makeBar(`${receiveLabel} — ${failureLabel}`, recvFailure, 'rgba(239,68,68,0.85)',  'receive'),
        makeBar(`${sendLabel} — ${successLabel}`,    sendSuccess, 'rgba(99,102,241,0.80)', 'send'),
        makeBar(`${sendLabel} — ${failureLabel}`,    sendFailure, 'rgba(249,115,22,0.85)', 'send'),
      ],
    };
  }

  // ─── Segment helpers ────────────────────────────────────────────────────────

  public segmentClass(seg: TimelineSegment): string {
    const total = seg.successCount + seg.failureCount;
    if (total === 0)            return 'bg-gray-100 dark:bg-dark-border';
    if (seg.failureCount === 0) return 'bg-emerald-400 dark:bg-emerald-500';
    if (seg.successCount === 0) return 'bg-red-400 dark:bg-red-500';
    return 'bg-amber-400 dark:bg-amber-500';
  }

  public onSegmentEnter(event: MouseEvent, seg: TimelineSegment): void {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this.tooltip = { segment: seg, x: rect.left + rect.width / 2, y: rect.top };
  }

  public onSegmentLeave(): void {
    this.tooltip = null;
  }

  // ─── Stats helpers ──────────────────────────────────────────────────────────

  public total(row: TimelineRow): number {
    return (row.aggregate?.successCount ?? 0) + (row.aggregate?.failureCount ?? 0);
  }

  public failures(row: TimelineRow): number {
    return row.aggregate?.failureCount ?? 0;
  }

  public lastSeen(row: TimelineRow): string {
    if (!row.aggregate) return '—';
    const when = row.aggregate.lastSeenUtc || row.aggregate.windowEndUtc;
    return when ? formatDate(when, DATE_TIME_FORMAT, this.locale) : '—';
  }

  public fmtHour(utc: string): string {
    return formatDate(utc, 'HH:mm', this.locale);
  }
}
